import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import {
  ArrowLeft,
  Save,
  User,
  Mail,
  Lock,
  DollarSign,
  AlertCircle,
  Eye,
  EyeOff,
  UserPlus
} from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';

const ClientForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showPassword, setShowPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [referrerSearch, setReferrerSearch] = useState('');
  const [referrerId, setReferrerId] = useState('');
  const isEditing = !!id;

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch
  } = useForm();

  // Cargar datos del cliente si estamos editando
  const { data: clientData, isLoading } = useQuery(
    ['client', id],
    () => api.get(`/users/${id}`).then(res => res.data.user),
    {
      enabled: isEditing,
      onSuccess: (data) => {
        reset({
          fullName: data.full_name,
          email: data.email,
          initialCapital: data.initial_capital,
          seedAmount: data.seed_amount,
          isActive: data.is_active
        });
        setReferrerId(data.referred_by || '');
      }
    }
  );

  // Candidatos a referidor (otros clientes)
  const { data: referrerCandidates } = useQuery(
    ['referrer-search', referrerSearch],
    () => api.get(`/users?search=${referrerSearch}&limit=10`).then(res => res.data.users),
    { enabled: referrerSearch.length > 0 }
  );

  // Actualizar referidor (modo edición)
  const referrerMutation = useMutation(
    (rid) => api.put(`/users/${id}/referrer`, { referrerId: rid || null }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['client', id]);
        toast.success('Referidor actualizado');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Error al actualizar el referidor');
      }
    }
  );

  const createMutation = useMutation(
    (data) => api.post('/users', data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('clients');
        toast.success('Cliente creado exitosamente');
        navigate('/admin/clients');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Error al crear cliente');
      }
    }
  );

  const updateMutation = useMutation(
    (data) => api.put(`/users/${id}`, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('clients');
        queryClient.invalidateQueries(['client', id]);
        toast.success('Cliente actualizado exitosamente');
        navigate('/admin/clients');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Error al actualizar cliente');
      }
    }
  );

  // Función para resetear la contraseña del cliente
  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    setResetLoading(true);
    try {
      await api.put(`/users/${id}/reset-password`, {
        newPassword: newPassword
      });
      toast.success('Contraseña reseteada exitosamente');
      setShowResetPassword(false);
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al resetear la contraseña');
    } finally {
      setResetLoading(false);
    }
  };

  const onSubmit = (data) => {
    const formData = {
      fullName: data.fullName,
      email: data.email,
      initialCapital: parseFloat(data.initialCapital),
      seedAmount: parseFloat(data.seedAmount),
      isActive: data.isActive
    };

    if (!isEditing) {
      formData.password = data.password;
      if (referrerId) formData.referrerId = parseInt(referrerId);
    }

    if (isEditing) {
      updateMutation.mutate(formData);
    } else {
      createMutation.mutate(formData);
    }
  };

  const seedAmountValue = watch('seedAmount');
  const initialCapitalValue = watch('initialCapital');

  // Calcular proyección de riesgo
  const calculateMaxSteps = (capital, seed) => {
    if (!capital || !seed || capital <= 0 || seed <= 0) return 0;

    let totalInvestment = 0;
    for (let step = 1; step <= 13; step++) {
      const stepAmount = seed * Math.pow(2, step - 1);
      totalInvestment += stepAmount;
      if (totalInvestment > capital) {
        return step - 1;
      }
    }
    return 13;
  };

  const maxSteps = calculateMaxSteps(initialCapitalValue, seedAmountValue);
  const riskLevel = maxSteps >= 10 ? 'low' : maxSteps >= 6 ? 'medium' : 'high';

  if (isLoading && isEditing) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8 text-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate('/admin/clients')}
          className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-800"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>

        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {isEditing ? 'Editar Cliente' : 'Nuevo Cliente'}
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            {isEditing ? 'Actualiza la información del cliente' : 'Crea un nuevo cliente en la plataforma'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Personal Information */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
            Información Personal
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Full Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Nombre Completo *
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  {...register('fullName', {
                    required: 'El nombre completo es requerido',
                    minLength: {
                      value: 2,
                      message: 'El nombre debe tener al menos 2 caracteres'
                    }
                  })}
                  className={`input-field pl-10 ${
                    errors.fullName ? 'border-danger-500 focus:ring-danger-500' : ''
                  }`}
                  placeholder="Juan Pérez"
                />
              </div>
              {errors.fullName && (
                <p className="mt-1 text-sm text-danger-600 dark:text-danger-400">
                  {errors.fullName.message}
                </p>
              )}
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Email *
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="email"
                  {...register('email', {
                    required: 'El email es requerido',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Email inválido'
                    }
                  })}
                  className={`input-field pl-10 ${
                    errors.email ? 'border-danger-500 focus:ring-danger-500' : ''
                  }`}
                  placeholder="juan@ejemplo.com"
                  disabled={isEditing}
                />
              </div>
              {errors.email && (
                <p className="mt-1 text-sm text-danger-600 dark:text-danger-400">
                  {errors.email.message}
                </p>
              )}
              {isEditing && (
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  El email no se puede modificar
                </p>
              )}
            </div>
          </div>

          {/* Password - Solo para nuevos clientes */}
          {!isEditing && (
            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Contraseña Inicial *
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  {...register('password', {
                    required: 'La contraseña es requerida',
                    minLength: {
                      value: 6,
                      message: 'La contraseña debe tener al menos 6 caracteres'
                    }
                  })}
                  className={`input-field pl-10 pr-10 ${
                    errors.password ? 'border-danger-500 focus:ring-danger-500' : ''
                  }`}
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-danger-600 dark:text-danger-400">
                  {errors.password.message}
                </p>
              )}
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                El cliente podrá cambiar esta contraseña después
              </p>
            </div>
          )}
        </motion.div>

        {/* Reset Password - Solo para edición */}
        {isEditing && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="card p-6"
          >
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Seguridad
            </h3>

            <div className="space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Restablece la contraseña del cliente si la ha olvidado.
              </p>

              {showResetPassword ? (
                <div className="space-y-4 p-4 bg-warning-50 dark:bg-warning-900/20 rounded-lg border border-warning-200 dark:border-warning-800">
                  <div className="flex items-start space-x-2">
                    <AlertCircle className="w-5 h-5 text-warning-600 dark:text-warning-400 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-warning-700 dark:text-warning-300">
                      La contraseña anterior será reemplazada. El cliente deberá usar la nueva contraseña para iniciar sesión.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Nueva Contraseña *
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="input-field pl-10 pr-10"
                        placeholder="••••••••"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Mínimo 6 caracteres
                    </p>
                  </div>

                  <div className="flex space-x-3">
                    <button
                      type="button"
                      onClick={() => {
                        setShowResetPassword(false);
                        setNewPassword('');
                      }}
                      className="flex-1 btn-secondary"
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      onClick={handleResetPassword}
                      disabled={resetLoading || !newPassword}
                      className="flex-1 btn-primary"
                    >
                      {resetLoading ? 'Reseteando...' : 'Confirmar Reseteo'}
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowResetPassword(true)}
                  className="btn-warning w-full md:w-auto"
                >
                  <Lock className="w-4 h-4 mr-2" />
                  Restablecer Contraseña
                </button>
              )}
            </div>
          </motion.div>
        )}

        {/* Financial Configuration */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
            Configuración Financiera
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Initial Capital */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Capital Inicial *
              </label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  {...register('initialCapital', {
                    required: 'El capital inicial es requerido',
                    min: {
                      value: 1,
                      message: 'El capital debe ser mayor a 0'
                    }
                  })}
                  className={`input-field pl-10 ${
                    errors.initialCapital ? 'border-danger-500 focus:ring-danger-500' : ''
                  }`}
                  placeholder="1000000"
                />
              </div>
              {errors.initialCapital && (
                <p className="mt-1 text-sm text-danger-600 dark:text-danger-400">
                  {errors.initialCapital.message}
                </p>
              )}
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Dinero total que el cliente deposita para trabajar
              </p>
            </div>

            {/* Seed Amount */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Monto de Semilla *
              </label>
              <select
                {...register('seedAmount', {
                  required: 'La semilla es requerida'
                })}
                className={`input-field ${
                  errors.seedAmount ? 'border-danger-500 focus:ring-danger-500' : ''
                }`}
              >
                <option value="">Seleccionar semilla...</option>
                <option value="1000">$1,000 (Conservadora)</option>
                <option value="3500">$3,500 (Intermedia)</option>
                <option value="5000">$5,000 (Intermedia)</option>
                <option value="10000">$10,000 (Agresiva)</option>
                <option value="15000">$15,000 (Agresiva)</option>
                <option value="20000">$20,000 (Muy Agresiva)</option>
              </select>
              {errors.seedAmount && (
                <p className="mt-1 text-sm text-danger-600 dark:text-danger-400">
                  {errors.seedAmount.message}
                </p>
              )}
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Apuesta inicial para el Paso 1
              </p>
            </div>
          </div>

          {/* Risk Projection */}
          {seedAmountValue && initialCapitalValue && (
            <div className="mt-6 p-4 bg-gray-50 dark:bg-dark-800 rounded-lg">
              <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">
                📊 Proyección de Riesgo
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {maxSteps}
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    Pasos Máximos
                  </p>
                </div>

                <div className="text-center">
                  <p className={`text-2xl font-bold ${
                    riskLevel === 'low' ? 'text-success-600 dark:text-success-400' :
                    riskLevel === 'medium' ? 'text-warning-600 dark:text-warning-400' :
                    'text-danger-600 dark:text-danger-400'
                  }`}>
                    {riskLevel === 'low' ? '🟢' : riskLevel === 'medium' ? '🟡' : '🔴'}
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    Nivel de Riesgo
                  </p>
                </div>

                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    ${(seedAmountValue * Math.pow(2, Math.min(maxSteps, 13) - 1)).toLocaleString()}
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    Apuesta Máxima
                  </p>
                </div>
              </div>

              <div className="mt-3 text-xs text-gray-600 dark:text-gray-400">
                <p>
                  <strong>Interpretación:</strong> Con este capital y semilla, el cliente puede
                  soportar hasta el paso {maxSteps} sin quedarse sin fondos.
                </p>
              </div>
            </div>
          )}
        </motion.div>

        {/* Programa de Referidos */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="card p-6"
        >
          <div className="flex items-center space-x-2 mb-4">
            <UserPlus className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Programa de Referidos
            </h3>
          </div>

          {isEditing && clientData?.referrer_name && (
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
              Referido actualmente por: <span className="font-medium text-gray-900 dark:text-gray-100">{clientData.referrer_name}</span>
            </p>
          )}

          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Referido por
          </label>
          <input
            type="text"
            value={referrerSearch}
            onChange={(e) => setReferrerSearch(e.target.value)}
            placeholder="Buscar cliente que lo refirió (nombre o email)..."
            className="input-field mb-2"
          />
          <select
            value={referrerId}
            onChange={(e) => setReferrerId(e.target.value)}
            className="input-field"
          >
            <option value="">Sin referidor</option>
            {(referrerCandidates || [])
              .filter(u => !isEditing || parseInt(u.id) !== parseInt(id))
              .map(u => (
                <option key={u.id} value={u.id}>{u.full_name} ({u.email})</option>
              ))}
          </select>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            El referidor recibe de por vida un % de las ganancias de este cliente (configurable en Ajustes).
          </p>

          {isEditing && (
            <button
              type="button"
              onClick={() => referrerMutation.mutate(referrerId)}
              disabled={referrerMutation.isLoading}
              className="mt-3 btn-secondary"
            >
              {referrerMutation.isLoading ? 'Guardando...' : 'Guardar referidor'}
            </button>
          )}
        </motion.div>

        {/* Status - Solo para edición */}
        {isEditing && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="card p-6"
          >
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Estado del Cliente
            </h3>

            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="isActive"
                {...register('isActive')}
                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <label htmlFor="isActive" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Cliente activo
              </label>
            </div>

            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              Los clientes inactivos no pueden participar en nuevos eventos
            </p>
          </motion.div>
        )}

        {/* Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="flex space-x-4"
        >
          <button
            type="button"
            onClick={() => navigate('/admin/clients')}
            className="flex-1 btn-secondary"
          >
            Cancelar
          </button>

          <button
            type="submit"
            disabled={createMutation.isLoading || updateMutation.isLoading}
            className="flex-1 btn-primary"
          >
            {createMutation.isLoading || updateMutation.isLoading ? (
              <div className="flex items-center justify-center space-x-2">
                <div className="spinner w-4 h-4"></div>
                <span>{isEditing ? 'Actualizando...' : 'Creando...'}</span>
              </div>
            ) : (
              <div className="flex items-center justify-center space-x-2">
                <Save className="w-4 h-4" />
                <span>{isEditing ? 'Actualizar Cliente' : 'Crear Cliente'}</span>
              </div>
            )}
          </button>
        </motion.div>
      </form>
    </div>
  );
};

export default ClientForm;